// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Button, CloseButton, Divider, Group, Modal, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconClipboardText, IconPhoneCall } from '@tabler/icons-react';
import type { JSX } from 'react';

import type { RemoteMonitoringEntry } from '../../hooks/useRemoteMonitoringQueue';

interface RpmContactInfoModalProps {
  entry: RemoteMonitoringEntry | undefined;
  opened: boolean;
  onClose: () => void;
  onAddNote: (entry: RemoteMonitoringEntry) => void;
}

function getTelecom(entry: RemoteMonitoringEntry | undefined, system: string): string {
  return entry?.telecom?.find((telecom) => telecom.system === system)?.value ?? '-';
}

function getAddress(entry: RemoteMonitoringEntry | undefined): string {
  const address = entry?.address?.[0];
  if (!address) {
    return '-';
  }
  const region = [address.state, address.postalCode].filter(Boolean).join(' ');
  return [address.line?.join(' '), address.city, region].filter(Boolean).join(', ');
}

function getExtensionValue(entry: RemoteMonitoringEntry | undefined, urlPart: string): string | undefined {
  const extension = entry?.extension?.find((item) => item.url?.toLowerCase().includes(urlPart));
  return (
    extension?.valueCodeableConcept?.text ??
    extension?.valueCodeableConcept?.coding?.[0]?.display ??
    extension?.valueString ??
    extension?.valueCode
  );
}

function formatContactUse(use: string | undefined): string | undefined {
  if (!use) {
    return undefined;
  }
  return use.charAt(0).toUpperCase() + use.slice(1);
}

function getPreferredContact(entry: RemoteMonitoringEntry | undefined): string {
  const preferredValue = getExtensionValue(entry, 'preferred-contact');
  if (preferredValue) {
    return preferredValue;
  }
  const ranked = [...(entry?.telecom ?? [])].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
  const first = ranked[0];
  if (first?.system === 'phone') {
    const use = formatContactUse(first.use);
    return use ? `Phone - ${use}` : 'Phone';
  }
  if (first?.system === 'email') {
    const use = formatContactUse(first.use);
    return use ? `Email - ${use}` : 'Email';
  }
  return '-';
}

function getBestTime(entry: RemoteMonitoringEntry | undefined): string {
  return getExtensionValue(entry, 'best-time') ?? getExtensionValue(entry, 'best-time-to-reach') ?? '-';
}

function DetailRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Box
      py="sm"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(130px, 0.42fr) 1fr',
        columnGap: 24,
        borderBottom: '1px solid var(--mantine-color-gray-2)',
      }}
    >
      <Text size="sm" fw={700} c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={600} c="gray.9">
        {value}
      </Text>
    </Box>
  );
}

export function RpmContactInfoModal({ entry, opened, onClose, onAddNote }: RpmContactInfoModalProps): JSX.Element {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size={550}
      padding={0}
      radius="md"
      withCloseButton={false}
      styles={{ content: { overflow: 'hidden' } }}
    >
      <Group justify="space-between" align="flex-start" px="xl" py="lg">
        <Group gap="md" align="flex-start">
          <ThemeIcon color="polaris" variant="light" radius="md" size={40}>
            <IconPhoneCall size={20} />
          </ThemeIcon>
          <Stack gap={2}>
            <Text fw={800} size="lg" lh={1.15}>
              {entry?.patientName ?? 'Patient contact'}
            </Text>
            <Text size="sm" c="dimmed">
              Patient contact information
            </Text>
          </Stack>
        </Group>
        <CloseButton onClick={onClose} aria-label="Close contact information" />
      </Group>

      <Divider />

      <Box px="xl" py="lg">
        <DetailRow label="Phone" value={getTelecom(entry, 'phone')} />
        <DetailRow label="Email" value={getTelecom(entry, 'email')} />
        <DetailRow label="Preferred contact" value={getPreferredContact(entry)} />
        <DetailRow label="Best time to reach" value={getBestTime(entry)} />
        <DetailRow label="Address" value={getAddress(entry)} />
      </Box>

      <Divider />

      <Group justify="flex-end" gap="sm" px="xl" py="lg">
        <Button variant="outline" color="polaris" onClick={onClose} miw={78}>
          Close
        </Button>
        <Button
          color="polaris"
          leftSection={<IconClipboardText size={16} />}
          disabled={!entry}
          onClick={() => {
            if (entry) {
              onAddNote(entry);
            }
          }}
        >
          Log contact & add note
        </Button>
      </Group>
    </Modal>
  );
}
